import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { DataTable } from "components/dataTable/index.jsx";
import { useEffect, useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react";
import Axios from "axios";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

const AVAILABLE_SCOPES = [
  { id: 'entertainment', label: 'Entertainment (Disney+)' },
  { id: 'snr', label: 'SNR' },
  { id: 'previsora', label: 'Previsora' },
  { id: 'tigogestion', label: 'Tigo Gestión' },
  { id: 'master', label: 'Master (Administrador del sistema)' },
];

export default observer(function AdminUsers(props) {
  const appDataStore = useAppDataStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Modal de opciones por usuario
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalTab, setModalTab] = useState('scopes');

  // Scopes
  const [editingScopes, setEditingScopes] = useState({});

  // Contraseña
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Historial ventas
  const [salesHistoryStartDate, setSalesHistoryStartDate] = useState("");
  const [salesHistoryEndDate, setSalesHistoryEndDate] = useState("");
  const [loadingExcel, setLoadingExcel] = useState(false);

  // Lista de precios
  const [priceListProducts, setPriceListProducts] = useState([]);
  const [priceListEdits, setPriceListEdits] = useState({});
  const [loadingPrices, setLoadingPrices] = useState(false);

  // Mapa de saldos para la tabla: { userId: balance }
  const [balancesMap, setBalancesMap] = useState({});

  // Saldo
  const [userBalances, setUserBalances] = useState([]);
  const [selectedBalanceType, setSelectedBalanceType] = useState(null);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [rechargeReason, setRechargeReason] = useState("");
  const [showRechargeForm, setShowRechargeForm] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [rechargesRefreshKey, setRechargesRefreshKey] = useState(0);

  // Crear producto Nuplin
  const [showCreateProductModal, setShowCreateProductModal] = useState(false);
  const [nuplinSkus, setNuplinSkus] = useState([]);
  const [loadingSkus, setLoadingSkus] = useState(false);
  const [createProductForm, setCreateProductForm] = useState({sku: '', skuLabel: '', price: '', retailPrice: '', validity: 1});
  const [savingProduct, setSavingProduct] = useState(false);

  // Crear usuario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: "",
    name: "",
    phone: "",
    password: "",
    scopes: {}
  });

  const userScopes = appDataStore.user?.scopes || [];
  const hasMasterScope = Array.isArray(userScopes) && userScopes.includes('master');

  const openUserModal = async (user, tab = 'sales') => {
    setSelectedUser(user);
    setModalTab(tab);
    // Inicializar scopes
    const scopesObj = {};
    user.scopes.forEach(s => { scopesObj[s] = true; });
    setEditingScopes(scopesObj);
    // Inicializar fechas historial
    const today = new Date();
    const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const fmt = d => d.toISOString().slice(0, 10);
    setSalesHistoryStartDate(fmt(firstOfMonth));
    setSalesHistoryEndDate(fmt(today));
    setNewPassword("");
    setConfirmPassword("");
    setShowRechargeForm(false);
    setRechargeAmount("");
    setRechargeReason("");

    // Cargar precios y saldo en paralelo
    setPriceListProducts([]);
    setPriceListEdits({});
    setUserBalances([]);
    setSelectedBalanceType(null);

    setLoadingPrices(true);
    setLoadingBalance(true);

    appDataStore.post("/users/user-products", {userId: user.id})
      .then(res => {
        const products = res.products || [];
        setPriceListProducts(products);
        const edits = {};
        products.forEach(p => { edits[p.id] = {price: p.price, retailPrice: p.retailPrice, providerId: p.providerId}; });
        setPriceListEdits(edits);
      })
      .catch(err => console.error("Error al cargar precios:", err))
      .finally(() => setLoadingPrices(false));

    appDataStore.post("/balance/user-balances", {userId: user.id})
      .then(res => {
        const balances = res.balances || [];
        setUserBalances(balances);
        if (balances.length > 0) setSelectedBalanceType(balances[0]);
      })
      .catch(err => console.error("Error al cargar saldo:", err))
      .finally(() => setLoadingBalance(false));
  };

  const closeModal = () => {
    setSelectedUser(null);
    setNewPassword("");
    setConfirmPassword("");
    setShowRechargeForm(false);
  };

  const handleRecharge = async (e) => {
    e.preventDefault();
    if (!rechargeAmount || parseFloat(rechargeAmount) <= 0) {
      alert("El monto debe ser mayor a 0");
      return;
    }
    try {
      setLoadingBalance(true);
      await appDataStore.post("/balance/recharge", {
        userId: selectedUser.id,
        type: selectedBalanceType.type,
        amount: parseFloat(rechargeAmount),
        reason: rechargeReason || null,
      });
      alert("Saldo recargado exitosamente");
      setRechargeAmount("");
      setRechargeReason("");
      setShowRechargeForm(false);
      setRechargesRefreshKey(prev => prev + 1);
      // Refrescar saldo
      const res = await appDataStore.post("/balance/user-balances", {userId: selectedUser.id});
      const balances = res.balances || [];
      setUserBalances(balances);
      const updated = balances.find(b => b.type === selectedBalanceType.type);
      if (updated) setSelectedBalanceType(updated);
      // Actualizar mapa de la tabla
      setBalancesMap(prev => ({
        ...prev,
        [selectedUser.id]: balances.reduce((sum, b) => sum + parseFloat(b.balance || 0), 0),
      }));
    } catch (err) {
      alert("Error al recargar saldo: " + err.message);
    } finally {
      setLoadingBalance(false);
    }
  };

  const rechargeHistoryColumns = useMemo(() => ([
    {accessorKey: 'createdAt', header: 'Fecha', cell: ({cell}) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm')},
    {accessorKey: 'amount', header: 'Monto', cell: ({cell}) => `+${formatters.cash(cell.getValue())}`},
    {accessorKey: 'previousBalance', header: 'Saldo Anterior', cell: ({cell}) => formatters.cash(cell.getValue())},
    {accessorKey: 'newBalance', header: 'Saldo Nuevo', cell: ({cell}) => formatters.cash(cell.getValue())},
    {accessorKey: 'reason', header: 'Detalles', cell: ({cell}) => cell.getValue() || '-'},
    {accessorKey: 'admin', header: 'Realizado Por', cell: ({cell}) => { const a = cell.getValue(); return a ? `${a.name} (${a.email})` : 'Sistema'; }},
  ]), []);

  const rechargeHistoryFetch = useCallback(async (records, currentPage, order, search, filter) => {
    if (!selectedUser) return {data: [], totalRows: 0, totalPages: 1};
    return appDataStore.post('/app/datasource/BalanceRecharge', {
      records, currentPage, order, search,
      attributes: ['id', 'amount', 'reason', 'previousBalance', 'newBalance', 'createdAt'],
      filter: [{key: 'userId', value: selectedUser.id, operator: 'eq'}],
      include: [{model: 'User', as: 'admin', attributes: ['id', 'name', 'email']}],
      searchAttributes: ['reason'],
    });
  }, [appDataStore, selectedUser?.id]);

  // Cargar usuarios al montar el componente
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await appDataStore.get('/users/list');
      const users = data.users || [];
      setUsers(users);
      // Cargar saldos de todos los usuarios en paralelo
      const results = await Promise.allSettled(
        users.map(u => appDataStore.post('/balance/user-balances', {userId: u.id}))
      );
      const map = {};
      results.forEach((r, i) => {
        if (r.status === 'fulfilled') {
          const balances = r.value.balances || [];
          map[users[i].id] = balances.reduce((sum, b) => sum + parseFloat(b.balance || 0), 0);
        }
      });
      setBalancesMap(map);
    } catch (err) {
      console.error('Error al cargar usuarios:', err);
      alert('Error al cargar los usuarios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleScopeChange = (scopeId) => {
    setEditingScopes(prev => ({...prev, [scopeId]: !prev[scopeId]}));
  };

  const handleSaveScopes = async () => {
    try {
      setLoading(true);
      const selectedScopes = Object.keys(editingScopes).filter(s => editingScopes[s]);
      await appDataStore.post('/users/update-scopes', {userId: selectedUser.id, scopes: selectedScopes});
      alert('Permisos actualizados exitosamente');
      await loadUsers();
    } catch (err) {
      alert('Error al actualizar permisos: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (userId) => {
    if (!newPassword) {
      alert("La contraseña es requerida");
      return;
    }

    if (newPassword.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      alert("Las contraseñas no coinciden");
      return;
    }

    try {
      setLoading(true);
      await appDataStore.post("/users/change-password", {
        userId,
        newPassword,
      });

      alert("Contraseña actualizada exitosamente");
      setNewPassword("");
      setConfirmPassword("");
      await loadUsers();
    } catch (err) {
      alert("Error al cambiar contraseña: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNewUserScopeChange = (scopeId) => {
    setNewUserData(prev => ({
      ...prev,
      scopes: {
        ...prev.scopes,
        [scopeId]: !prev.scopes[scopeId]
      }
    }));
  };

  const handleCreateUser = async () => {
    if (!newUserData.email) {
      alert("El email es requerido");
      return;
    }

    if (!newUserData.email.includes("@")) {
      alert("Ingresa un email válido");
      return;
    }

    if (!newUserData.name) {
      alert("El nombre es requerido");
      return;
    }

    if (!newUserData.password) {
      alert("La contraseña es requerida");
      return;
    }

    if (newUserData.password.length < 6) {
      alert("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    try {
      setLoading(true);
      const selectedScopes = Object.keys(newUserData.scopes).filter(scope => newUserData.scopes[scope]);

      await appDataStore.post("/users/create", {
        email: newUserData.email,
        name: newUserData.name,
        phone: newUserData.phone || null,
        password: newUserData.password,
        scopes: selectedScopes
      });

      alert("Usuario creado exitosamente");
      setShowCreateForm(false);
      setNewUserData({
        email: "",
        name: "",
        phone: "",
        password: "",
        scopes: {}
      });
      await loadUsers();
    } catch (err) {
      debugger
      alert("Error al crear usuario: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewUserData({
      email: "",
      name: "",
      phone: "",
      password: "",
      scopes: {}
    });
  };

  const openCreateProductModal = async () => {
    setCreateProductForm({sku: '', skuLabel: '', price: '', retailPrice: '', validity: 1});
    setShowCreateProductModal(true);
    if (nuplinSkus.length === 0) {
      setLoadingSkus(true);
      try {
        const res = await appDataStore.get('/nuplin-tokens/skus');
        const skus = res.skus || [];
        setNuplinSkus(Array.isArray(skus) ? skus.map(s => ({
          value: s.api_name,
          label: `${s.traduccion_paquete || s.name} (${s.tipo})`,
        })) : []);
      } catch (err) {
        console.error('Error cargando SKUs:', err);
      } finally {
        setLoadingSkus(false);
      }
    }
  };

  const handleCreateProduct = async () => {
    const {sku, skuLabel, price, retailPrice, validity} = createProductForm;
    if (!sku) return alert('Selecciona un SKU');
    if (!price || parseFloat(price) < 0) return alert('Ingresa un precio costo válido');
    if (!retailPrice || parseFloat(retailPrice) < 0) return alert('Ingresa un precio venta válido');
    try {
      setSavingProduct(true);
      await appDataStore.post('/users/create-product', {
        userId: selectedUser.id,
        category: 'nuplin',
        providerId: sku,
        name: skuLabel || sku,
        price: parseFloat(price),
        retailPrice: parseFloat(retailPrice),
        validity: parseInt(validity) || 1,
      });
      alert('Producto creado exitosamente');
      setShowCreateProductModal(false);
      // Refrescar lista de precios
      setLoadingPrices(true);
      const res = await appDataStore.post('/users/user-products', {userId: selectedUser.id});
      const products = res.products || [];
      setPriceListProducts(products);
      const edits = {};
      products.forEach(p => { edits[p.id] = {price: p.price, retailPrice: p.retailPrice, providerId: p.providerId}; });
      setPriceListEdits(edits);
      setLoadingPrices(false);
    } catch (err) {
      alert('Error al crear producto: ' + err.message);
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDeleteProduct = async (productId, productName) => {
    if (!confirm(`¿Eliminar el producto "${productName}"?`)) return;
    try {
      setLoadingPrices(true);
      await appDataStore.post('/users/delete-product', {productId});
      const res = await appDataStore.post('/users/user-products', {userId: selectedUser.id});
      const products = res.products || [];
      setPriceListProducts(products);
      const edits = {};
      products.forEach(p => { edits[p.id] = {price: p.price, retailPrice: p.retailPrice}; });
      setPriceListEdits(edits);
    } catch (err) {
      alert('Error al eliminar producto: ' + err.message);
    } finally {
      setLoadingPrices(false);
    }
  };

  const handlePriceEdit = (productId, field, value) => {
    setPriceListEdits(prev => ({...prev, [productId]: {...prev[productId], [field]: value}}));
  };

  const handleSaveAllPrices = async () => {
    try {
      setLoadingPrices(true);
      for (const product of priceListProducts) {
        const edit = priceListEdits[product.id];
        if (edit.price !== product.price || edit.retailPrice !== product.retailPrice || edit.providerId !== product.providerId) {
          await appDataStore.post("/users/update-product-price", {
            productId: product.id,
            price: edit.price,
            retailPrice: edit.retailPrice,
            providerId: edit.providerId,
          });
        }
      }
      alert("Precios actualizados exitosamente");
    } catch (err) {
      alert("Error al guardar precios: " + err.message);
    } finally {
      setLoadingPrices(false);
    }
  };

  const handleDownloadSalesHistory = async () => {
    if (!salesHistoryStartDate || !salesHistoryEndDate) {
      alert("Debes seleccionar la fecha de inicio y fin");
      return;
    }
    if (salesHistoryStartDate > salesHistoryEndDate) {
      alert("La fecha de inicio no puede ser mayor a la fecha de fin");
      return;
    }
    try {
      setLoadingExcel(true);
      const response = await Axios.post(
        "/api/v1/users/sales-history",
        { data: { userId: selectedUser.id, startDate: salesHistoryStartDate, endDate: salesHistoryEndDate } },
        {
          headers: { authorization: `Token ${appDataStore.user?.token}` },
          responseType: "arraybuffer",
        }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const disposition = response.headers["content-disposition"] || "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `historial_ventas_${salesHistoryStartDate}_${salesHistoryEndDate}.xlsx`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Error al descargar el historial: " + (err.message || "Error desconocido"));
    } finally {
      setLoadingExcel(false);
    }
  };

  if (!hasMasterScope) {
    return (
      <>
        <div className="p-2 max-w-7xl mx-auto text-black">
          <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Usuarios</h2>
          <div className="bg-red-50 border border-red-300 rounded-lg p-6 max-w-2xl">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🔒</span>
              <div>
                <h3 className="text-lg font-bold text-red-800 mb-2">Acceso Denegado</h3>
                <p className="text-red-700">
                  No tienes permisos para acceder a la gestión de usuarios. Se requiere el scope <strong>Master</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const MODAL_TABS = [
    {id: 'sales', label: 'Historial ventas'},
    {id: 'recharges', label: 'Historial recargas'},
    {id: 'scopes', label: 'Permisos'},
    {id: 'password', label: 'Contraseña'},
    {id: 'prices', label: 'Lista de precios'},
  ];

  return (
    <>
      <div className="p-2 max-w-7xl mx-auto text-black">
        <h2 className="text-2xl font-bold mb-4 text-gray-800">Gestión de Usuarios</h2>

        <div className="mb-6">
          <button
            onClick={() => setShowCreateForm(true)}
            disabled={loading}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium"
          >
            + Crear nuevo usuario
          </button>
        </div>

        {/* Modal crear usuario */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-lg">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-gray-800">Crear nuevo usuario</h3>
                <button onClick={handleCancelCreate} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                  <input type="email" placeholder="usuario@ejemplo.com" value={newUserData.email}
                    onChange={(e) => setNewUserData(prev => ({...prev, email: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre *</label>
                  <input type="text" placeholder="Juan Pérez" value={newUserData.name}
                    onChange={(e) => setNewUserData(prev => ({...prev, name: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Teléfono (Opcional)</label>
                  <input type="tel" placeholder="+57 3001234567" value={newUserData.phone}
                    onChange={(e) => setNewUserData(prev => ({...prev, phone: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña *</label>
                  <input type="password" placeholder="Mínimo 6 caracteres" value={newUserData.password}
                    onChange={(e) => setNewUserData(prev => ({...prev, password: e.target.value}))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-800 mb-3">Permisos</label>
                  <div className="space-y-2 bg-gray-50 p-3 rounded border border-gray-300">
                    {AVAILABLE_SCOPES.map((scope) => (
                      <label key={scope.id} className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={newUserData.scopes[scope.id] || false}
                          onChange={() => handleNewUserScopeChange(scope.id)}
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                        <span className="text-sm text-gray-700">{scope.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex gap-3 mt-6">
                <button onClick={handleCreateUser} disabled={loading}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium">
                  Crear Usuario
                </button>
                <button onClick={handleCancelCreate} disabled={loading}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium">
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de opciones de usuario */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-[95vw] max-h-[95vh] flex flex-col shadow-xl">
              {/* Header */}
              <div className="flex justify-between items-start px-6 pt-5 pb-4 border-b border-gray-200">
                <div>
                  <h3 className="text-lg font-bold text-gray-800">{selectedUser.name}</h3>
                  <p className="text-sm text-gray-500">{selectedUser.email}</p>
                </div>
                <button onClick={closeModal} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none ml-4">×</button>
              </div>

              {/* Pestañas internas */}
              <div className="flex border-b border-gray-200 px-6">
                {MODAL_TABS.map(tab => (
                  <button key={tab.id} onClick={() => setModalTab(tab.id)}
                    className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${modalTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Contenido */}
              <div className="flex-1 overflow-y-auto px-6 py-5">

                {/* Pestaña: Permisos */}
                {modalTab === 'scopes' && (
                  <div>
                    <p className="text-sm text-gray-600 mb-4">Selecciona los módulos a los que tiene acceso este usuario.</p>
                    <div className="space-y-3 bg-gray-50 p-4 rounded border border-gray-200">
                      {AVAILABLE_SCOPES.map((scope) => (
                        <label key={scope.id} className="flex items-center gap-3 cursor-pointer">
                          <input type="checkbox" checked={editingScopes[scope.id] || false}
                            onChange={() => handleScopeChange(scope.id)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer" />
                          <span className="text-sm text-gray-700">{scope.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="flex gap-3 mt-5">
                      <button onClick={handleSaveScopes} disabled={loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                        {loading ? 'Guardando...' : 'Guardar permisos'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pestaña: Contraseña */}
                {modalTab === 'password' && (
                  <div className="max-w-sm">
                    <p className="text-sm text-gray-600 mb-4">Establece una nueva contraseña para este usuario.</p>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Nueva contraseña</label>
                        <input type="password" placeholder="Mínimo 6 caracteres" value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Confirmar contraseña</label>
                        <input type="password" placeholder="Repite la contraseña" value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                      </div>
                    </div>
                    <div className="mt-5">
                      <button onClick={() => handleChangePassword(selectedUser.id)} disabled={loading}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                        {loading ? 'Guardando...' : 'Cambiar contraseña'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pestaña: Historial recargas */}
                {modalTab === 'recharges' && (
                  <DataTable
                    key={`recharges-${selectedUser?.id}-${rechargesRefreshKey}`}
                    columns={rechargeHistoryColumns}
                    dataFetch={rechargeHistoryFetch}
                    defaultSorting={[{id: 'createdAt', desc: true}]}
                    exportFileName={`recargas_${selectedUser?.name}_${moment().format('DDMMYY')}`}
                  />
                )}

                {/* Pestaña: Historial ventas */}
                {modalTab === 'sales' && (
                  <div className="max-w-sm">
                    <p className="text-sm text-gray-600 mb-3">Descarga el historial de ventas de este distribuidor en formato Excel.</p>
                    <div className="flex gap-2 mb-4">
                      {[
                        {label: 'Hoy', fn: () => { const t = moment().format('YYYY-MM-DD'); setSalesHistoryStartDate(t); setSalesHistoryEndDate(t); }},
                        {label: 'Ayer', fn: () => { setSalesHistoryStartDate(moment().subtract('1','day').endOf('day').format('YYYY-MM-DD')); setSalesHistoryEndDate(moment().subtract(1,'day').endOf('day').format('YYYY-MM-DD')); }},
                        {label: 'Mes actual', fn: () => { setSalesHistoryStartDate(moment().startOf('month').format('YYYY-MM-DD')); setSalesHistoryEndDate(moment().format('YYYY-MM-DD')); }},
                        {label: 'Mes anterior', fn: () => { setSalesHistoryStartDate(moment().subtract(1,'month').startOf('month').format('YYYY-MM-DD')); setSalesHistoryEndDate(moment().subtract(1,'month').endOf('month').format('YYYY-MM-DD')); }},
                      ].map(({label, fn}) => (
                        <button key={label} onClick={fn}
                          className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 text-gray-700 rounded border border-gray-300">
                          {label}
                        </button>
                      ))}
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de inicio</label>
                        <input type="date" value={salesHistoryStartDate}
                          onChange={(e) => setSalesHistoryStartDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Fecha de fin</label>
                        <input type="date" value={salesHistoryEndDate}
                          onChange={(e) => setSalesHistoryEndDate(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
                      </div>
                    </div>
                    <div className="mt-5">
                      <button onClick={handleDownloadSalesHistory} disabled={loadingExcel}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                        {loadingExcel ? 'Descargando...' : 'Descargar Excel'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Pestaña: Lista de precios */}
                {modalTab === 'prices' && (
                  <div>
                    <div className="flex justify-end mb-3">
                      <button onClick={openCreateProductModal}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium">
                        + Producto Nuplin
                      </button>
                    </div>
                    {loadingPrices ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Cargando productos...</div>
                    ) : priceListProducts.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">No hay productos para este usuario.</div>
                    ) : (
                      <>
                        <table className="w-full text-sm border-collapse">
                          <thead className="sticky top-0 bg-gray-100">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">ID</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Producto</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Cat.</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Meses</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Provider ID</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Precio costo</th>
                              <th className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300">Precio venta</th>
                              <th className="px-3 py-2 border-b border-gray-300"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {priceListProducts.map(product => (
                              <tr key={product.id} className="border-b border-gray-200 hover:bg-gray-50">
                                <td className="px-3 py-2 text-gray-500 text-xs font-mono">{product.id}</td>
                                <td className="px-3 py-2 text-gray-800 font-medium">{product.name}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${product.category === 'nuplin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                                    {product.category}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-600 text-center">{product.validity}</td>
                                <td className="px-3 py-2">
                                  <input type="text"
                                    value={priceListEdits[product.id]?.providerId ?? product.providerId ?? ''}
                                    onChange={e => handlePriceEdit(product.id, 'providerId', e.target.value)}
                                    className="w-80 px-2 py-1 border border-gray-300 rounded text-sm font-mono" />
                                </td>
                                <td className="px-3 py-2">
                                  <input type="number" step="0.01"
                                    value={priceListEdits[product.id]?.price ?? product.price}
                                    onChange={e => handlePriceEdit(product.id, 'price', e.target.value)}
                                    className="w-28 px-2 py-1 border border-gray-300 rounded text-sm" />
                                </td>
                                <td className="px-3 py-2">
                                  <input type="number" step="0.01"
                                    value={priceListEdits[product.id]?.retailPrice ?? product.retailPrice}
                                    onChange={e => handlePriceEdit(product.id, 'retailPrice', e.target.value)}
                                    className="w-28 px-2 py-1 border border-gray-300 rounded text-sm" />
                                </td>
                                <td className="px-3 py-2">
                                  {product.category === 'nuplin' && (
                                    <button onClick={() => handleDeleteProduct(product.id, product.name)}
                                      className="text-red-500 hover:text-red-700 text-xs font-medium">
                                      Eliminar
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="mt-4 pt-4 border-t border-gray-200">
                          <button onClick={handleSaveAllPrices} disabled={loadingPrices}
                            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                            {loadingPrices ? 'Guardando...' : 'Guardar cambios'}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {loading && users.length === 0 ? (
          <div className="text-center py-8 text-gray-600">Cargando usuarios...</div>
        ) : users.length === 0 ? (
          <div className="text-center py-8 text-gray-600">No hay usuarios</div>
        ) : (
          <div className="overflow-x-auto border border-gray-300 rounded-lg">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Nombre</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Email</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Teléfono</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Permisos</th>
                  <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Saldo</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700"></th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-800">{user.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{user.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-500">{user.phone || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1.5">
                        {user.scopes && user.scopes.length > 0 ? (
                          user.scopes.map((scope) => (
                            <span key={scope} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                              {AVAILABLE_SCOPES.find(s => s.id === scope)?.label || scope}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">Sin permisos</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      {balancesMap[user.id] !== undefined ? (
                        <span className={`text-sm font-bold ${balancesMap[user.id] > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {formatters.cash(balancesMap[user.id])}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => openUserModal(user)}
                        className="px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white rounded text-sm font-medium"
                      >
                        Opciones
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Crear producto Nuplin */}
      {showCreateProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-800">Crear producto Nuplin</h3>
              <button onClick={() => setShowCreateProductModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl font-bold leading-none">×</button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SKU *</label>
                {loadingSkus ? (
                  <div className="text-sm text-gray-500 py-2">Cargando SKUs...</div>
                ) : (
                  <select
                    value={createProductForm.sku}
                    onChange={e => {
                      const selected = nuplinSkus.find(s => s.value === e.target.value);
                      setCreateProductForm(prev => ({...prev, sku: e.target.value, skuLabel: selected?.label || e.target.value}));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  >
                    <option value="">Selecciona un SKU</option>
                    {nuplinSkus.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                )}
                {createProductForm.sku && (
                  <p className="text-xs text-gray-500 mt-1">api_name: {createProductForm.sku}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio costo *</label>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={createProductForm.price}
                  onChange={e => setCreateProductForm(prev => ({...prev, price: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Precio venta *</label>
                <input type="number" step="0.01" min="0" placeholder="0.00"
                  value={createProductForm.retailPrice}
                  onChange={e => setCreateProductForm(prev => ({...prev, retailPrice: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vigencia (meses) *</label>
                <input type="number" min="1" max="24" placeholder="1"
                  value={createProductForm.validity}
                  onChange={e => setCreateProductForm(prev => ({...prev, validity: e.target.value}))}
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleCreateProduct} disabled={savingProduct || !createProductForm.sku}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded font-medium text-sm">
                {savingProduct ? 'Creando...' : 'Crear producto'}
              </button>
              <button onClick={() => setShowCreateProductModal(false)} disabled={savingProduct}
                className="flex-1 px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-100 font-medium text-sm">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});
